import { Trans, useLingui } from "@lingui/react/macro";

import { share } from "../../model/change";
import type { DashboardFunnel } from "../../model/TeamDashboard";
import { DashboardCard } from "./DashboardCard";

type Step = { label: string; value: number; of: number | null; ofLabel: string | null };

type FunnelProps = {
  funnel: DashboardFunnel;
};

// «Путь нового игрока» за период: сколько дошло до каждого шага и доля от предыдущего.
// Без Umami первого шага («Зашли на сайт») нет. «Открыли разбор» — хоть раз открыли готовый
// разбор своего матча. «Вернулись через неделю» — доля от тех, кто пришёл не позже чем
// 7 дней назад.
export const Funnel = ({ funnel }: FunnelProps) => {
  const { i18n, t } = useLingui();
  const number = new Intl.NumberFormat(i18n.locale);
  const percent = new Intl.NumberFormat(i18n.locale, {
    style: "percent",
    maximumFractionDigits: 0,
  });
  const visited = t`Visited the site`;
  const signedIn = t`Signed in via Steam`;
  const loaded = t`Match history loaded`;
  const analyzed = t`Analyzed a match`;
  const viewed = t`Opened the analysis`;
  const steps: Step[] = [
    ...(funnel.visitors === null
      ? []
      : [{ label: visited, value: funnel.visitors, of: null, ofLabel: null }]),
    {
      label: signedIn,
      value: funnel.signed_in,
      of: funnel.visitors,
      ofLabel: funnel.visitors === null ? null : visited,
    },
    { label: loaded, value: funnel.history_loaded, of: funnel.signed_in, ofLabel: signedIn },
    { label: analyzed, value: funnel.analyzed, of: funnel.history_loaded, ofLabel: loaded },
    { label: viewed, value: funnel.viewed, of: funnel.analyzed, ofLabel: analyzed },
    { label: t`Did a drill`, value: funnel.drilled, of: funnel.viewed, ofLabel: viewed },
  ];
  const widest = Math.max(1, ...steps.map((step) => step.value));
  const returned = share(funnel.returned, funnel.returned_eligible);
  const eligible = funnel.returned_eligible;

  return (
    <DashboardCard
      title={<Trans>New player journey</Trans>}
      description={<Trans>How many reached each step, and the share of the previous one</Trans>}
    >
      <ol className="flex flex-col gap-2.5">
        {steps.map((step) => {
          const stepShare = share(step.value, step.of);
          const shareText = stepShare === null ? null : percent.format(stepShare);
          const from = step.ofLabel?.toLowerCase();
          return (
            <li key={step.label} className="flex flex-col gap-1">
              <span className="flex justify-between gap-3 text-[13px]">
                <span className="text-foreground-soft">{step.label}</span>
                {shareText !== null && from !== undefined && (
                  <span className="text-subtle">{t`${shareText} of “${from}”`}</span>
                )}
              </span>
              <span className="flex items-center gap-2.5">
                <span className="flex h-3.5 min-w-0 flex-1 items-center">
                  <span
                    className="h-3 min-w-1 rounded-r bg-chart-amber"
                    style={{ width: `${String((step.value / widest) * 100)}%` }}
                  />
                </span>
                <span className="min-w-13 text-right font-mono text-sm font-semibold tabular-nums">
                  {number.format(step.value)}
                </span>
              </span>
            </li>
          );
        })}
        <li className="flex justify-between gap-3 border-t border-border/60 pt-2.5 text-[13px]">
          <span className="text-foreground-soft">
            <Trans>Came back a week later</Trans>
          </span>
          <span className="font-mono font-semibold">
            {returned === null ? "—" : percent.format(returned)}
          </span>
        </li>
      </ol>
      <p className="text-xs text-subtle">
        <Trans>
          “Opened the analysis” — opened a finished analysis of their match at least once. “Came
          back a week later” counts the {eligible} players who joined at least 7 days ago.
        </Trans>
      </p>
    </DashboardCard>
  );
};
